import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  CaptureMode,
  CaptureWorkflow,
  JobAttachment,
  QualityGateResult,
  VoiceNoteContentType,
  VoiceNotePurpose,
} from '../api/types';

export type QueuedCaptureStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

/**
 * One frame of a capture.
 *
 * A capture carries one asset or several: the item, its label, its rating
 * plate. Each frame has its own sha256, because the presign and the
 * server's duplicate gate work per asset, so the same rating plate
 * photographed twice never uploads its bytes twice.
 *
 * The bytes are an ArrayBuffer rather than a Blob. IndexedDB accepts both,
 * but a Blob survives a structured clone only where the runtime implements
 * it for Blob, and Safari has shipped releases that lose Blob values
 * written to IndexedDB. An ArrayBuffer is clonable everywhere.
 * `contentType` carries the type the Blob would have, and the upload path
 * rebuilds one from the pair.
 */
export interface QueuedAsset {
  fingerprint: string;
  contentType: string;
  bytes: ArrayBuffer;
}

/**
 * A capture waiting to leave the device. This is the resilience control
 * (BUILD_PLAN SC3 note 1): everything a capture needs to finish uploading
 * lives in this record, including the raw asset bytes, so the queue
 * survives an app restart with no dependency on anything still being in
 * memory.
 */
export interface QueuedCapture {
  /** Client-generated, the IndexedDB primary key. Stable across retries. */
  localId: string;
  status: QueuedCaptureStatus;
  attempts: number;
  lastError?: string;
  /**
   * Set when the API refused the capture with 402 because the account has no
   * credits. The row is failed at once, because retrying changes nothing
   * until credits are added, and `lastError` holds the API's own wording.
   * `duplicate_in_job` is the 409 for a photo the job already holds, which
   * no retry can change either.
   */
  refusal?: 'payment_required' | 'duplicate_in_job';
  createdAt: string;
  updatedAt: string;
  mode: CaptureMode;
  /** The job the operator picked before capturing. Travels to extraction. */
  workflow?: CaptureWorkflow;
  /**
   * The capture's own fingerprint, the server's idempotency key for
   * `POST /capture`. Derived from the ordered asset fingerprints
   * (src/lib/fingerprint.ts `captureFingerprint`), so the same set of frames
   * resubmitted lands on the same capture and a different set does not.
   */
  fingerprint: string;
  /** In capture order. Never empty. */
  assets: QueuedAsset[];
  /**
   * The Capture Job the capture belongs to (CAPTURE_JOBS.md section 1).
   * Every capture taken on this build carries one. Optional only because a
   * row queued by an earlier build, before jobs existed, has none, and
   * `processQueue` stops that row rather than sending a capture the API
   * refuses with a 400.
   */
  captureJobId?: string;
  sessionId: string;
  workItemId?: string;
  siteId?: string;
  teamId?: string;
  voiceTranscript?: string;
  clientQuality?: QualityGateResult;
  manualFields?: Record<string, string>;
  /** Set once `POST /capture` succeeds. */
  captureId?: string;
}

export type QueuedVoiceNoteStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

/**
 * One voice clip waiting to reach `POST /capture/{id}/voice-notes`
 * (INTERFACES.md 15.3). Recorded with no connection, or against a capture
 * that has not reached the server yet, it holds its own bytes the same way
 * `QueuedAsset` does, so it survives a reload with no dependency on the
 * screen that recorded it.
 *
 * A note is keyed to its capture one of two ways, never both. `captureId`
 * is set when the capture already has a server id at record time, which is
 * every voice note recorded from the review screen's refine panel.
 * `queuedCaptureLocalId` is set when the note is recorded before the
 * capture itself has been submitted, which is the viewfinder's mic: it
 * points at the `queue` row the note travels with, and is resolved to a
 * `captureId` once that row uploads (`processVoiceNoteQueue`,
 * voice-queue.ts).
 */
export interface QueuedVoiceNote {
  localId: string;
  status: QueuedVoiceNoteStatus;
  attempts: number;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
  captureId?: string;
  queuedCaptureLocalId?: string;
  /** Absent on a live-transcription note, which carries text only. */
  contentType?: VoiceNoteContentType;
  byteLength: number;
  durationMs: number;
  purpose: VoiceNotePurpose;
  languageCode?: string;
  /** Absent on a live-transcription note, which carries text only. */
  bytes?: ArrayBuffer;
  /**
   * The final text of a live transcription (CAPTURE_JOBS.md section 3).
   * A note carrying it has no audio: it registers with
   * `POST /capture/{id}/voice-notes` straight away, with no presign and no
   * upload, under the `noteId` minted when it was queued.
   */
  transcript?: string;
  /** Set once `POST /capture/{id}/voice-notes/upload-url` has answered. */
  noteId?: string;
}

/**
 * The last attachment list the API answered for a job (CAPTURE_JOBS.md
 * section 8.5), without its download URLs, which expire. It lets the job
 * view show the job's files after a restart with no signal.
 */
export interface SavedAttachmentList {
  jobId: string;
  items: JobAttachment[];
  savedAt: string;
}

/**
 * One job attachment saved on the device so the operator can open it with
 * no signal. An attachment's bytes never change (a replaced file is a new
 * attachment with a new id), so a saved copy never goes stale. Bytes are an
 * ArrayBuffer for the same Safari reason as `QueuedAsset`.
 */
export interface SavedAttachmentFile {
  attachmentId: string;
  jobId: string;
  fileName: string;
  contentType: string;
  byteLength: number;
  bytes: ArrayBuffer;
  savedAt: string;
  /** Updated when the operator opens it, so eviction drops unused files first. */
  lastUsedAt: string;
}

interface SmartCaptureOfflineDb extends DBSchema {
  queue: {
    key: string;
    value: QueuedCapture;
    indexes: { 'by-status': QueuedCaptureStatus };
  };
  voiceNotes: {
    key: string;
    value: QueuedVoiceNote;
    indexes: { 'by-status': QueuedVoiceNoteStatus };
  };
  attachmentLists: {
    key: string;
    value: SavedAttachmentList;
  };
  attachmentFiles: {
    key: string;
    value: SavedAttachmentFile;
    indexes: { 'by-job': string };
  };
}

const DB_NAME = 'smart-capture-offline';
/**
 * 2: a capture holds an array of assets rather than one set of bytes, so an
 * operator can send several angles of one item as one capture.
 * 3: adds the `voiceNotes` store (INTERFACES.md 15.3, W1). A separate store
 * rather than a field on `QueuedCapture`, because a voice note recorded
 * from the review screen's refine panel has a `captureId` with no local
 * `queue` row at all when the capture was taken on a different device.
 * 4: adds `attachmentLists` and `attachmentFiles`, the job attachments saved
 * on the device so an operator opens them with no signal
 * (src/lib/offline/attachment-cache.ts).
 */
const DB_VERSION = 4;

/** The v1 shape, read only by the upgrade below. */
interface QueuedCaptureV1 {
  fingerprint: string;
  contentType: string;
  bytes: ArrayBuffer;
}

let dbPromise: Promise<IDBPDatabase<SmartCaptureOfflineDb>> | null = null;

export function getOfflineDb(): Promise<IDBPDatabase<SmartCaptureOfflineDb>> {
  if (!dbPromise) {
    dbPromise = openDB<SmartCaptureOfflineDb>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          const store = db.createObjectStore('queue', { keyPath: 'localId' });
          store.createIndex('by-status', 'status');
        }
        if (oldVersion === 1) {
          // Captures already queued on a device that has been offline are
          // the whole reason this queue exists, so they are migrated rather
          // than dropped: a v1 record's single asset becomes a one-element
          // list, and its fingerprint stays the capture's own.
          //
          // That fingerprint was the asset's sha256 used directly, where a
          // capture taken on this build hashes its asset fingerprints again
          // (src/lib/fingerprint.ts `captureFingerprint`). The migrated one
          // is left alone: it is still unique to that capture and still
          // stable across retries, which is all the server's idempotency key
          // asks of it, and re-deriving it here is not open to us anyway,
          // because `crypto.subtle.digest` is asynchronous and awaiting a
          // non-IndexedDB promise inside an upgrade transaction closes it.
          const store = transaction.objectStore('queue');
          for (const record of await store.getAll()) {
            const legacy = record as unknown as QueuedCaptureV1;
            if (Array.isArray((record as QueuedCapture).assets)) continue;
            (record as QueuedCapture).assets = [
              {
                fingerprint: legacy.fingerprint,
                contentType: legacy.contentType,
                bytes: legacy.bytes,
              },
            ];
            delete (legacy as Partial<QueuedCaptureV1>).contentType;
            delete (legacy as Partial<QueuedCaptureV1>).bytes;
            await store.put(record);
          }
        }
        if (oldVersion < 3) {
          const store = db.createObjectStore('voiceNotes', { keyPath: 'localId' });
          store.createIndex('by-status', 'status');
        }
        if (oldVersion < 4) {
          db.createObjectStore('attachmentLists', { keyPath: 'jobId' });
          const files = db.createObjectStore('attachmentFiles', { keyPath: 'attachmentId' });
          files.createIndex('by-job', 'jobId');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Test-only: closes the open connection and forces the next
 * `getOfflineDb()` call to open a fresh one. The underlying IndexedDB data
 * is untouched, so calling this and then reading again is the same
 * operation a real page reload performs against a real browser's
 * IndexedDB.
 *
 * The close matters. Dropping the handle alone leaves the connection open,
 * and an open connection blocks `deleteDatabase` and every version-change
 * transaction after it, which hangs the next open instead of failing it.
 */
export async function _resetConnectionForTesting(): Promise<void> {
  const pending = dbPromise;
  dbPromise = null;
  if (pending) {
    const db = await pending;
    db.close();
  }
}
